/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColDef} from 'ag-grid-community';
import _ from 'lodash';
import {translate} from '@ion/core/src/locale/locale';
import Devices from '@ion/core/src/services/Device';
import {Device} from '@ion/core/src/types/entities';
import {DownloadTemplate, TemplateColumn} from '@ion/core/src/types/templates';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {formatAddress} from '@ion/dls/src/utils/ComponentUtils';

// defines the type Definitions for procssed column definitions ...
export type ProcessedColDef = {
  [key: string]: string;
  field: string;
  key: string;
};

/**
 * Provides a utility which process the excel columns for mapping row values against
 * each column so that the same can be downloaded as templates with pre-populated values.
 *
 * ```typescript
 *
 *   import {processExportColumns} from '@ion/dls/src/utils/';
 *
 *   const columns = [{
 *     "header": "Group Name",
 *      "key": "groupName",
 *      "field": "group"
 *     },
 *     {
 *     "header": "Asset Name",
 *     "key": "name"
 *     },
 *     {
 *     "header": "IO Profile",
 *     "key": "ioProfile",
 *     "field": "ioProfileUI"
 *   }];
 *
 *   const processedColumns = processExportColumns(columns);
 *   // [{
 *         "headerName": "Group Name",
 *         "key": "groupName",
 *         "field": "group"
 *        },
 *        {
 *         "headerName": "Asset Name",
 *         "key": "name",
 *         "field": "name"
 *        },
 *        {
 *         "header": "IO Profile",
 *         "key": "ioProfile",
 *         "field": "ioProfileUI"
 *       }];
 * ```
 */
export const processExportColumns = (
  templateColumns: TemplateColumn[],
  columnKey: string = 'headerName'
): ProcessedColDef[] => {
  const customColumns = [];
  templateColumns?.forEach((column: TemplateColumn) => {
    const {header, key, field: fieldUI} = column;
    const customColumn = {
      [columnKey] : header,
      field       : fieldUI || key,
      key
    };
    customColumns.push(customColumn);
  });
  return customColumns;
};

/**
 * Determines which columns to return.
 *
 * If the download template has columns they will take precedence, otherwise returns the first arg columns.
 */
export const determineExportColumns = (
  columns: ColDef[],
  {columns: templateColumns}: DownloadTemplate
): ColDef[] | ProcessedColDef[] =>
  templateColumns ? processExportColumns(templateColumns) : columns;

export const getLastReportedDate = (device: Device, latestIdReport: any, formatter: Function = formatDateTime) => {
  const reportDateArr = [
    'lastReportedTime',
    'extensions.idReportTimestamp',
    'idReport.device.updateTime',
    'lastIdReport',
    'lastAvlCommunication.locationTime'
  ];

  let reportedDate;
  let reportedDateStr;
  let lastIdreport;
  if (latestIdReport?.length) {
    const reportedDate = latestIdReport[0]?.locoEvent;
    lastIdreport = formatter(reportedDate?.eventTime);
  }
  if (device) {
    for (const reportDate of reportDateArr) {
      reportedDateStr = _.get(device, reportDate);
      if (reportedDateStr) {
        break;
      }
    }
    reportedDate = (reportedDateStr && typeof reportedDateStr === 'string') ? formatter(reportedDateStr) : '';
  }
  return reportedDate || lastIdreport || '';
};

export const getLastAddress = (device: Device) => {
  const addressArr = [
    'lastAvlCommunication.address',
    'lastKnownGoodAvlPosition.address'
  ];

  let address;
  if (device) {
    for (const addr of addressArr) {
      address = _.get(device, addr);
      if (address) {
        break;
      }
    }
  }
  return address ? formatAddress(address) : '';
};

/**
 * Provides a utility which for dispatching selected row from grid
 *
 * ```typescript
 *
 *   import {onRowClicked} from '@ion/dls/src/utils/';
 *
 *   const dispatchFn = (data) => dispatch(setSelectedAsset(data));
 *   const onRowClickHandler = onRowClicked(dispatchFn, rowRef);
 *
 *   <IonGrid ref={ionGridRef}
        onRowClicked={onRowClicked} />
 *
 * ```
 */
export const onAssetRowClicked = function(dispatchFn, rowRef) {
  return function(data) {
    const {api, node, data: asset} = data;
    rowRef.current = true;
    api?.deselectAll();
    node?.setSelected(true, true);
    const [assetDevice] = asset.isScDevice
      ? Devices.getDevicesFromCollection([asset], true)
      : Devices.getAssetsFromCollection([asset]);
    dispatchFn(assetDevice);
  };
};

// Types of available grid column filters
export enum GridFilterType {
  TEXT = 'agTextColumnFilter',
  DATE = 'agDateColumnFilter',
  NUMBER = 'agNumberColumnFilter'
}

/**
 * Utility that determines if the given column name should be filtered.
 * If a filter type is not provided, it will default to text filtering.
 */
export const determineColumnFilter = (
  column: string,
  filteredColumns: string[],
  filterType: GridFilterType = GridFilterType.TEXT
): GridFilterType => {
  return filteredColumns.includes(column) ? filterType : undefined;
};

/**
 * Utility that determines the color of battery icon based on percentage value.
 */
export const getBatteryLevelColor = (
  value: number) => {
  if (value >= 50) {
    return dlsColor('caGreen');
  }
  if (value >= 25 && value < 50) {
    return dlsColor('caYellow');
  }
  if (value < 25 && value >= 0) {
    return dlsColor('caRed');
  }
};

/**
 * Utility that returns visibility status as true/false for provided fields.
 */
export const columnVisibility = (columns: ColDef[], fields: string[]): Record<string, boolean> => {
  const visibility: Record<string, boolean> = {};
  columns.forEach(({field, hide}) => {
    if (fields?.includes(field)) {
      visibility[field] = !hide;
    }
  });
  return visibility;
};

/**
 * Utility that determines if Heart beat and Test Vin APIs should be called based on
 * column visibility and isICNUser and hasVehicleProfile properties.
 */
export const verifySupplementalCalls = ({
  columns,
  isICNUser,
  reloadOnVisible,
  hasVehicleProfile,
  hasInheritableGroups
}) => {
  const {
    firstIcn = false,
    lastIcn = false,
    vehicleProfile = false,
    groupList = false
  } = columnVisibility(columns, reloadOnVisible);
  return {
    includeHeartBeat         : isICNUser && (firstIcn || lastIcn),
    includeVin               : hasVehicleProfile && vehicleProfile,
    includeInheritableGroups : hasInheritableGroups && groupList
  };
};

/**
 * Retrieves the options for filtering the state column.
 * @returns An array of state options for filtering.
 *
 * ```typescript
 *
 *  import {getStatusFilterParams} from './GridUtils';
 *
 *  const options = getStatusFilterParams();
 *  console.log(options);
 *
 * ```
 */
export const getStatusFilterParams = () => {
  const statusFilterList = translate('common:modularsearch.status', {returnObjects: true});
  return {values: Object.values(statusFilterList)};
};
